// WHAT: Modern session flow component with enhanced UI and step navigation for Miracle Flow sessions
// WHY:  Provides improved user experience for session workflow with progress tracking, step validation, and session persistence
// DOMAIN: Productivity → Miracle Flow → Session Management
// DEPENDENCIES: StepCard component, sessionApi service, React hooks, session types
// RISKS:
//   - NOTE: Step validation uses imperative handle - may not catch all validation errors
//   - NOTE: Session state persistence is asynchronous - may lose data on navigation
//   - WARN: Step navigation logic is complex - may allow invalid state transitions
//   - NOTE: Exercise video URL is passed through props - may not be available for all sessions

import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Square,
  Sparkles,
  Clock,
  Target,
} from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";

import { Card, Button, Stack, Heading, ProgressBar, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

import { type FlowStep, type Session } from "../../types";
import { sessionApi } from "../../utils/api-client";
import { STEP_NAMES, STEP_DESCRIPTIONS, STEP_ICONS } from "../../utils/constants";
import { StepCard, type StepCardReference } from "../molecules/StepCard";

interface ModernSessionFlowProperties {
  readonly exerciseVideoUrl?: null | string;
  readonly onSessionCancel?: () => void;
  readonly onSessionComplete?: () => void;
  readonly session: Session;
  readonly sessionType: "Evening" | "Monthly" | "Morning" | "Weekly";
}

/**
 * WHAT:
 *   Modern session flow component with enhanced UI and step navigation for Miracle Flow sessions.
 *
 * WHY:
 *   Provides improved user experience for session workflow with progress tracking, step validation, and session persistence.
 *
 * PROPS:
 *   - session: Session — Current session object
 *   - sessionType: 'Morning' | 'Evening' | 'Monthly' | 'Weekly' — Type of session
 *   - onSessionComplete?: () => void — Callback when session is completed
 *   - onSessionCancel?: () => void — Callback when session is cancelled
 *   - exerciseVideoUrl?: string | null — Optional YouTube URL for exercise step
 *
 * BEHAVIOR:
 *   - Determines steps based on session type
 *   - Tracks current step index and completed steps
 *   - Provides step navigation (next/previous)
 *   - Shows progress bar and step indicators
 *   - Validates steps before allowing progression
 *   - Persists session state to database
 *
 * RISKS:
 *   - NOTE: Step validation uses imperative handle - may not catch all validation errors
 *   - NOTE: Session state persistence is asynchronous - may lose data on navigation
 *   - WARN: Step navigation logic is complex - may allow invalid state transitions
 *   - NOTE: Exercise video URL is passed through props - may not be available for all sessions
 *
 * @param root0
 *
 * @param root0.session
 *
 * @param root0.sessionType
 *
 * @param root0.onSessionComplete
 *
 * @param root0.onSessionCancel
 *
 * @param root0.exerciseVideoUrl
 *
 * @returns JSX.Element
 *
 */
export function ModernSessionFlow({
  exerciseVideoUrl,
  onSessionCancel,
  onSessionComplete,
  session,
  sessionType,
}: ModernSessionFlowProperties) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<FlowStep>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const stepCardReference = useRef<StepCardReference>(null);

  // NOTE: Get steps based on session type - memoized to prevent infinite re-renders
  // PERF: useMemo ensures steps array is only recalculated when sessionType changes
  const steps = useMemo((): FlowStep[] => {
    switch (sessionType) {
      case "Morning": {
        return ["Meditation", "Affirmations", "Visualization", "Exercise", "Reading", "Scribing"];
      }
      case "Evening": {
        return ["EveningReflection", "EveningAffirmations", "Visualization", "EveningMeditation"];
      }
      case "Monthly": {
        return ["MonthlyCheck"];
      }
      case "Weekly": {
        return ["WeeklyReflection"];
      }
      default: {
        return [];
      }
    }
  }, [sessionType]);

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  // NOTE: Calculate progress percentage - currentStepIndex + 1 because index is 0-based
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  // NOTE: Initialize current step based on session state from database
  // This allows resuming sessions that were interrupted
  useEffect(() => {
    if (steps.includes(session.current_step)) {
      const stepIndex = steps.indexOf(session.current_step);
      setCurrentStepIndex(stepIndex);
    }

    // NOTE: Mark completed steps from session state
    const completed = new Set<FlowStep>();
    for (const step of session.completed_steps) {
      if (steps.includes(step)) {
        completed.add(step);
      }
    }
    setCompletedSteps(completed);

    // Set session start time
    if (session.created_at) {
      // NOTE: Safely parse created_at to avoid passing arbitrary values into Date constructor
      const createdAt = session.created_at;
      if (typeof createdAt === "string" || typeof createdAt === "number") {
        setSessionStartTime(new Date(createdAt));
      }
    }
  }, [session, steps]);

  // Convert FlowStep (PascalCase) to snake_case for backend
  const stepToSnakeCase = (step: FlowStep): string => {
    const stepMap: Record<FlowStep, string> = {
      Affirmations: "affirmations",
      EveningAffirmations: "evening_affirmations",
      EveningMeditation: "evening_meditation",
      EveningReflection: "evening_reflection",
      Exercise: "exercise",
      JournalQuestions: "journal_questions",
      Meditation: "meditation",
      MonthlyCheck: "monthly_check",
      Reading: "reading",
      Scribing: "scribing",
      Visualization: "visualization",
      WeeklyReflection: "weekly_reflection",
    };
    return (
      stepMap[step] ||
      step
        .toLowerCase()
        .replaceAll(/([A-Z])/g, "_$1")
        .toLowerCase()
        .replace(/^_/, "")
    );
  };

  const handleStepComplete = async () => {
    if (!currentStep) {
      return;
    }

    setIsLoading(true);
    try {
      // Convert FlowStep to snake_case for backend API
      const stepString = stepToSnakeCase(currentStep);
      await sessionApi.completeStep(stepString);
      setCompletedSteps((previous) => new Set([...previous, currentStep]));

      // Short pause for visual feedback (1 second)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Auto-navigate to next step
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex((previous) => previous + 1);
      } else {
        // Session completed
        await sessionApi.finishSession();
        onSessionComplete?.();
      }
    } catch {
      // NOTE: Failed to complete step - error is intentionally not surfaced to avoid console usage
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepSkip = async () => {
    if (!currentStep) {
      return;
    }

    setIsLoading(true);
    try {
      // Convert FlowStep to snake_case for backend API
      const stepString = stepToSnakeCase(currentStep);
      await sessionApi.completeStep(stepString); // Skip is same as complete

      // Move to next step
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex((previous) => previous + 1);
      } else {
        // Session completed
        await sessionApi.finishSession();
        onSessionComplete?.();
      }
    } catch {
      // NOTE: Failed to skip step - error is intentionally not surfaced to avoid console usage
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((previous) => previous - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((previous) => previous + 1);
    }
  };

  const handleCancelSession = async () => {
    setIsLoading(true);
    try {
      // Use stopSession which cancels/ends the current active session on backend
      await sessionApi.stopSession();
      onSessionCancel?.();
    } catch {
      // NOTE: Failed to cancel session - error is intentionally not surfaced to avoid console usage
    } finally {
      setIsLoading(false);
    }
  };

  const getStepIcon = (step: FlowStep) => {
    return STEP_ICONS[step] || "📝";
  };

  const getStepName = (step: FlowStep) => {
    return STEP_NAMES[step] || step;
  };

  // Get shortened step name for smaller screens
  const getShortStepName = (step: FlowStep): string => {
    const fullName = getStepName(step);
    // Return first word or very short version
    const shortNames: Record<FlowStep, string> = {
      Affirmations: "Affirm",
      EveningAffirmations: "Affirm",
      EveningMeditation: "Meditate",
      EveningReflection: "Reflect",
      Exercise: "Exercise",
      JournalQuestions: "Journal",
      Meditation: "Meditate",
      MonthlyCheck: "Monthly",
      Reading: "Read",
      Scribing: "Write",
      Visualization: "Visualize",
      WeeklyReflection: "Weekly",
    };
    const shortName = shortNames[step];
    if (shortName) {
      return shortName;
    }
    const firstWord = fullName.split(" ")[0];
    return firstWord ?? fullName;
  };

  // Get very short step name (1-2 words max)
  const getVeryShortStepName = (step: FlowStep): string => {
    const fullName = getStepName(step);
    const veryShortNames: Record<FlowStep, string> = {
      Affirmations: "Affirm",
      EveningAffirmations: "Affirm",
      EveningMeditation: "Med",
      EveningReflection: "Reflect",
      Exercise: "Ex",
      JournalQuestions: "Journal",
      Meditation: "Med",
      MonthlyCheck: "Month",
      Reading: "Read",
      Scribing: "Write",
      Visualization: "Viz",
      WeeklyReflection: "Week",
    };
    return veryShortNames[step] || fullName.slice(0, 4) || fullName;
  };

  const getStepDescription = (step: FlowStep) => {
    return STEP_DESCRIPTIONS[step] || "Complete this step to continue";
  };

  const getSessionDuration = () => {
    if (!sessionStartTime) {
      return "0:00";
    }
    const now = new Date();
    const diff = now.getTime() - sessionStartTime.getTime();
    const minutes = Math.floor(diff / 60_000);
    const seconds = Math.floor((diff % 60_000) / 1000);
    return `${String(minutes)}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-6 w-full">
      {/* Modern Progress Header */}
      <Card
        className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-[#334155] w-full"
        padding="lg"
        variant="elevated"
      >
        <Stack direction="col" gap={6}>
          {/* Session Info */}
          <Stack align="center" direction="row" justify="between">
            <Stack direction="col" gap={2}>
              <Stack align="center" direction="row" gap={3}>
                <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-bright-blue to-tracq-deep-blue shadow-tracq-card">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <Stack direction="col" gap={1}>
                  <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
                    {sessionType} Session
                  </Heading>
                  <p className="text-[#94A3B8] text-sm">
                    Step {currentStepIndex + 1} of {totalSteps} • {Math.round(progress)}% Complete
                  </p>
                </Stack>
              </Stack>
            </Stack>

            <Stack align="center" direction="row" gap={4}>
              {/* Session Duration */}
              <div className="flex items-center gap-2 px-3 py-2 bg-[#334155] rounded-tracq border border-[#475569]">
                <Clock className="w-4 h-4 text-tracq-bright-blue" />
                <span className="text-sm font-medium text-[#F1F5F9]">{getSessionDuration()}</span>
              </div>

              {/* Progress Badge */}
              <Badge
                className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
                size="lg"
                variant="info"
              >
                <Target className="w-4 h-4 mr-1" />
                {Math.round(progress)}%
              </Badge>
            </Stack>
          </Stack>

          {/* Modern Progress Bar */}
          <div className="space-y-3">
            <ProgressBar
              className="h-3"
              showLabel={false}
              state="default"
              value={progress}
              variant="striped"
            />

            {/* Step Indicators */}
            <div className="flex justify-between gap-1 sm:gap-2">
              {steps.map((step, index) => (
                <div
                  className={cn(
                    "flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-tracq transition-all duration-300 ease-in-out flex-1",
                    "hover:bg-[#334155] cursor-pointer",
                    index === currentStepIndex &&
                      "bg-tracq-bright-blue/20 border border-tracq-bright-blue/30",
                    completedSteps.has(step) && "bg-green-500/20 border border-green-500/30",
                  )}
                  key={step}
                  onClick={() => {
                    setCurrentStepIndex(index);
                  }}
                >
                  <div
                    className={cn(
                      "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg transition-all duration-300",
                      "shadow-tracq-card flex-shrink-0",
                      (() => {
                        if (completedSteps.has(step)) {
                          return "bg-green-500 text-white shadow-green-500/30";
                        }
                        if (index === currentStepIndex) {
                          return "bg-tracq-bright-blue text-white shadow-tracq-bright-blue/30";
                        }
                        return "bg-[#334155] text-[#94A3B8]";
                      })(),
                    )}
                  >
                    {completedSteps.has(step) ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <span className="text-base sm:text-lg">{getStepIcon(step)}</span>
                    )}
                  </div>
                  {/* Responsive Text: Very short on mobile, short on sm, medium on md, full on lg+ */}
                  <span
                    className={cn(
                      "block text-[10px] sm:text-xs md:text-sm text-center font-medium transition-colors duration-200",
                      "px-1 break-words leading-tight",
                      "max-w-[50px] sm:max-w-[70px] md:max-w-[90px] lg:max-w-[120px] xl:max-w-none",
                      index === currentStepIndex ? "text-tracq-bright-blue" : "text-[#94A3B8]",
                      completedSteps.has(step) && "text-green-400",
                    )}
                  >
                    {/* Very short on mobile (< sm), short on sm, medium on md, full on lg+ */}
                    <span className="sm:hidden">{getVeryShortStepName(step)}</span>
                    <span className="hidden sm:inline md:hidden">{getShortStepName(step)}</span>
                    <span className="hidden md:inline lg:hidden">
                      {getStepName(step).split(" ")[0]}
                    </span>
                    <span className="hidden lg:inline">{getStepName(step)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Stack>
      </Card>

      {/* Current Step Card */}
      {currentStep ? (
        <Card
          className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-[#334155] w-full"
          padding="lg"
          variant="elevated"
        >
          <Stack className="w-full" direction="col" gap={6}>
            {/* Step Header */}
            <Stack align="center" className="w-full" direction="row" gap={4}>
              <div className="flex items-center justify-center w-16 h-16 rounded-tracq bg-gradient-to-br from-tracq-bright-blue to-tracq-deep-blue shadow-tracq-card flex-shrink-0">
                <span className="text-2xl">{getStepIcon(currentStep)}</span>
              </div>
              <Stack className="flex-1 min-w-0" direction="col" gap={2}>
                <Heading className="text-[#F1F5F9] font-bold" level="h3" size="xl">
                  {getStepName(currentStep)}
                </Heading>
                <p className="text-[#CBD5E1] text-lg">{getStepDescription(currentStep)}</p>
              </Stack>
            </Stack>

            {/* Step Content */}
            <StepCard
              exerciseVideoUrl={exerciseVideoUrl ?? null}
              isLoading={isLoading}
              onComplete={() => {
                // NOTE: Wrap async completion to avoid passing Promise directly to callback prop
                void handleStepComplete();
              }}
              onSkip={() => {
                void handleStepSkip();
              }}
              ref={stepCardReference}
              status={completedSteps.has(currentStep) ? "completed" : "active"}
              step={currentStep}
            />
          </Stack>
        </Card>
      ) : null}

      {/* Modern Navigation Controls */}
      <Card className="bg-[#1E293B] border-[#334155] w-full" padding="lg" variant="default">
        <Stack align="center" direction="row" justify="between">
          {/* Previous/Next Controls */}
          <Stack direction="row" gap={3}>
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50 hover:text-tracq-bright-blue transition-all duration-200"
              disabled={currentStepIndex === 0 || isLoading}
              onClick={handlePreviousStep}
              size="md"
              variant="outline"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50 hover:text-tracq-bright-blue transition-all duration-200"
              disabled={currentStepIndex === totalSteps - 1 || isLoading}
              onClick={handleNextStep}
              size="md"
              variant="outline"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Stack>

          {/* Session Controls */}
          <Stack direction="row" gap={3}>
            <Button
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500 hover:text-red-300 transition-all duration-200"
              disabled={isLoading}
              onClick={() => {
                void handleCancelSession();
              }}
              size="md"
              variant="outline"
            >
              <Square className="w-4 h-4 mr-2" />
              Cancel Session
            </Button>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}
